import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shippingApi } from '../services/api';
import { ShippingRecord } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { formatVietnamCurrency } from '../utils/numberFormatter';

const DeletedRecordsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [records, setRecords] = useState<ShippingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    licensePlate: '',
    shippingAddress: '',
    province: ''
  });

  const fetchDeletedRecords = async () => {
    try {
      const response = await shippingApi.getDeleted();
      setRecords(response.data);
    } catch (error) {
      console.error('Error fetching deleted records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id: number) => {
    if (!window.confirm(t('confirm.restore.record'))) return;
    try {
      await shippingApi.restore(id);
      fetchDeletedRecords();
      alert(t('record.restored.successfully'));
    } catch (error) {
      alert(t('failed.to.restore.record'));
    }
  };

  const handleView = (record: ShippingRecord) => {
    // Navigate to a view-only page (you might want to create this)
    navigate(`/shipping/view/${record.id}`);
  };

  useEffect(() => {
    fetchDeletedRecords();
  }, []);

  if (loading) {
    return <div className="container mt-4"><div className="spinner-border"></div></div>;
  }

  // Filter records based on filters
  const filteredRecords = records.filter(record => {
    const matchesLicense = !filters.licensePlate || record.licensePlate.toLowerCase().includes(filters.licensePlate.toLowerCase());
    const matchesAddress = !filters.shippingAddress || record.shippingAddress.toLowerCase().includes(filters.shippingAddress.toLowerCase());
    const matchesProvince = !filters.province || record.shippingAddress.toLowerCase().includes(filters.province.toLowerCase());
    return matchesLicense && matchesAddress && matchesProvince;
  });

  return (
    <div className="w-100 p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <h1>{t('deleted.records')}</h1>
        </div>
        <div>
          <button className="btn btn-outline-secondary" onClick={() => navigate('/shipping')}>
            {t('back.to.shipping')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-3 w-100">
        <div className="card-body">
          <h6 className="card-title">{t('filters')}</h6>
          <div className="row">
            <div className="col-md-4">
              <label className="form-label">{t('license.plate')}</label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by license plate..."
                value={filters.licensePlate}
                onChange={(e) => setFilters({ ...filters, licensePlate: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">{t('shipping.address')}</label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by shipping address..."
                value={filters.shippingAddress}
                onChange={(e) => setFilters({ ...filters, shippingAddress: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">{t('province')}</label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by province..."
                value={filters.province}
                onChange={(e) => setFilters({ ...filters, province: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Deleted Records */}
      <div className="card w-100">
        <div className="card-body">
          <h5 className="card-title text-danger">{t('deleted.records')}</h5>
          {filteredRecords.length === 0 ? (
            <p className="text-muted">{t('no.deleted.records')}</p>
          ) : (
            <div className="table-responsive w-100">
              <table className="table w-100">
                <thead>
                  <tr>
                    <th>{t('date')}</th>
                    <th style={{ minWidth: '100px' }}>{t('truck')}</th>
                    <th>{t('destination')}</th>
                    <th>{t('destinationProvince')}</th>
                    <th>{t('shipping.cost')}</th>
                    <th>{t('advance')}</th>
                    <th>{t('loading.fee')}</th>
                    <th>{t('toll.fee')}</th>
                    <th>{t('fuel.cost')}</th>
                    <th>{t('rm2')}</th>
                    <th>{t('repair.cost')}</th>
                    <th>{t('total.fee')}</th>
                    <th>{t('deleted.by')}</th>
                    <th>{t('deleted.at')}</th>
                    <th style={{ minWidth: '190px' }}>{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <tr key={record.id}>
                      <td>{new Date(record.shippingDate).toLocaleDateString()}</td>
                      <td>{record.licensePlate}</td>
                      <td>{record.shippingAddress}</td>
                      <td>{record.provinceName || '-'}</td>
                      <td>{typeof record.shippingCost === 'number' ? formatVietnamCurrency(record.shippingCost) : '-'}</td>
                      <td>{typeof record.prepaidAmount === 'number' ? formatVietnamCurrency(record.prepaidAmount) : '-'}</td>
                      <td>{typeof record.loadUnloadFee === 'number' ? formatVietnamCurrency(record.loadUnloadFee) : '-'}</td>
                      <td>{typeof record.tollFee === 'number' ? formatVietnamCurrency(record.tollFee) : '-'}</td>
                      <td>{typeof record.fuelCost === 'number' ? formatVietnamCurrency(record.fuelCost) : '-'}</td>
                      <td>{typeof record.rM2Fee === 'number' ? formatVietnamCurrency(record.rM2Fee) : '-'}</td>
                      <td>{typeof record.repairCost === 'number' ? formatVietnamCurrency(record.repairCost) : '-'}</td>
                      <td>{typeof record.totalFee === 'number' ? formatVietnamCurrency(record.totalFee) : '-'}</td>
                      <td>{record.deletedByUserName || '-'}</td>
                      <td>{record.deletedAt ? new Date(record.deletedAt).toLocaleDateString() : '-'}</td>
                      <td style={{ minWidth: '190px' }}>
                        <button 
                          className="btn btn-sm btn-outline-info me-2" 
                          onClick={() => handleView(record)}
                        >
                          {t('view')}
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-success" 
                          onClick={() => handleRestore(record.id)}
                        >
                          {t('restore')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeletedRecordsPage;